import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';

import CustomAutoComplete from '../CustomAutoComplete/CustomAutoComplete';
import { generateFilterOptions } from '../../utils/Utils';

const modalContainerStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: '983px',
  height: 'auto'
};

const modalStyle = {
  bgcolor: '#FBFCFE',
  boxShadow: '0px 2px 24px 8px #005CBC1F',
  padding: '40px',
  border: '10px solid #D7E5F8',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const closeIconStyle = {
  color: '#FFFFFF',
  fontSize: '22px',
  cursor: 'pointer'
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
};

const filterButtonsContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end'
};

const AdvancedFilter = ({ columns, open, onClose, data, selectedFilters, onFilterChange }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="Upload File"
      aria-describedby="Excel file upload">
      <Box sx={modalContainerStyle}>
        <Box sx={{ textAlign: 'end' }}>
          <CloseIcon onClick={onClose} sx={closeIconStyle} />
        </Box>
        <Box sx={modalStyle}>
          <FilterCategories
            columns={columns}
            data={data}
            selectedFilters={selectedFilters}
            onFilterChange={onFilterChange}
            onClose={onClose}
          />
        </Box>
      </Box>
    </Modal>
  );
};

const FilterCategories = ({ columns, data, selectedFilters, onFilterChange, onClose }) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [tempSelectedFilters, setTempSelectedFilters] = useState(selectedFilters);
  const [flag, setFlag] = useState(false);

  const categories = [...new Set(columns.filter((col) => col.category).map((col) => col.category))];

  const filteredData = (filters) =>
    data.filter((row) => {
      for (const column in filters) {
        // eslint-disable-next-line no-prototype-builtins
        if (filters.hasOwnProperty(column)) {
          const columnFilter = filters[column];
          const isAnyFilterSelected = columnFilter?.some((checked) => checked);
          if (isAnyFilterSelected && !columnFilter.includes(row[column])) {
            return false;
          }
        }
      }
      return true;
    });

  const handleTempFilterChange = (columnId, selectedValues) => {
    const updatedFilters = {
      ...tempSelectedFilters,
      [columnId]: selectedValues
    };
    setTempSelectedFilters(updatedFilters);

    // update options
    const newFilteredData = filteredData(updatedFilters);
    const filters = columns.reduce((acc, col) => {
      acc[col.id] =
        col.id !== columnId
          ? [...new Set(newFilteredData.map((row) => row[col.id]))]
          : [...filterOptions[col.id]]; // not change current column options
      return acc;
    }, {});
    setFilterOptions(filters);
  };

  const handleClearFilter = () => {
    setTempSelectedFilters({});
    setFlag(true);
  };

  const handleApplyFilters = () => {
    onFilterChange(tempSelectedFilters);
    onClose();
  };

  useEffect(() => {
    const newFilterOptions = generateFilterOptions(columns, data);
    setFilterOptions(newFilterOptions);
    setTempSelectedFilters(selectedFilters);
  }, [columns, data, selectedFilters]);
  useEffect(() => {
    if (flag) {
      onFilterChange(tempSelectedFilters);
      setFlag(false);
    }
  }, [tempSelectedFilters]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 8,
          px: 4,
          pb: 4,
          mb: 4,
          maxHeight: '60vh',
          overflow: 'auto',
          flexDirection: 'column'
        }}>
        {categories.map((category) => (
          <Box key={category} sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 5 }}>
              {category}
            </Typography>
            <Grid container columnSpacing={4} rowSpacing={2} columns={12}>
              {columns
                .filter((col) => col.category === category)
                .map((col, index) => (
                  <Grid key={col?.id} item xs={6}>
                    <CustomAutoComplete
                      placeholder="Select"
                      label={col?.label}
                      size="large"
                      options={filterOptions[col.id] ?? []}
                      selectedOptions={tempSelectedFilters[col.id] ?? []}
                      onOptionsChange={(selectedValues) =>
                        handleTempFilterChange(col.id, selectedValues)
                      }
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        ))}
      </Box>
      <Box sx={buttonContainerStyle}>
        <Button
          size="small"
          variant="contained"
          data-testid="closeBtn"
          sx={{
            mr: 1,
            backgroundColor: '#117D9F',
            '&:hover': {
              backgroundColor: '#117D9F'
            }
          }}
          onClick={onClose}>
          Close
        </Button>
        <Box sx={filterButtonsContainerStyle}>
          <Button
            size="small"
            color="primary"
            data-testid="clear-all-filters-btn"
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={handleClearFilter}>
            Clear All Filters
          </Button>
          <Button
            size="small"
            color="primary"
            data-testid="apply-filters-btn"
            variant="contained"
            sx={{ mr: 1 }}
            onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AdvancedFilter;
